import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useQuery } from 'react-apollo';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import EditIcon from '@material-ui/icons/Edit';
import AddIcon from '@material-ui/icons/Add';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { FIND_ALL_CONVENIO_COMERCIAL } from '../../../../graphql/queryes';
import ModalSlider from '../../../../components/modal-slider/modal-slider';
import ModalConvenioAlternativo from './ModalConvenioAlternativo';
import Notification from '../../../../components/notification/Notification';

const ConveniosAlternativo = ({ unidade }) => {
  const [modalAberto, setModalAberto] = useState(false);
  const [selecionado, setSelecionado] = useState(null);
  const [notification, setNotification] = useState({ isOpen: false, variant: '', message: '' });

  const frontWebUrl = unidade?.rede?.whitelabel?.frontWebUrl;

  const { data, loading, refetch } = useQuery(FIND_ALL_CONVENIO_COMERCIAL, {
    variables: { unidadeId: unidade?.id, searchDTO: {}, pageableDTO: { pageSize: 9999 } },
    fetchPolicy: 'no-cache',
    skip: !unidade?.id,
  });

  const convenios = data?.findAllConvenioComercial?.content || [];

  const handleEditar = (item) => {
    setSelecionado(item);
    setModalAberto(true);
  };

  const handleAdicionar = () => {
    setSelecionado(null);
    setModalAberto(true);
  };

  const handleClose = () => {
    setModalAberto(false);
    setSelecionado(null);
  };

  const handleSuccess = () => {
    refetch();
  };

  const buildLink = (slug) => {
    if (!frontWebUrl || !slug) return null;
    const baseUrl = frontWebUrl.endsWith('/') ? frontWebUrl.slice(0, -1) : frontWebUrl;
    return `${baseUrl}/pesquisa/consulta?parceiro=${slug}`;
  };

  const handleCopiarLink = async (slug) => {
    const link = buildLink(slug);
    if (!link) return;

    try {
      await navigator.clipboard.writeText(link);
      setNotification({ isOpen: true, variant: 'success', message: 'Link copiado para a área de transferência' });
    } catch {
      setNotification({ isOpen: true, variant: 'error', message: 'Erro ao copiar link' });
    }
  };

  return (
    <Container>
      <Header>
        <Title>Convênios</Title>
        <AddButton
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdicionar}
        >
          Novo Convênio
        </AddButton>
      </Header>

      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Descrição</Th>
              <Th>Slug</Th>
              <Th>Link Parceiro</Th>
              <Th>Wallet ID</Th>
              <Th>% Repasse</Th>
              <Th>Ativo</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {convenios.map((item) => {
              const slug = item.slugAsaas;
              return (
                <tr key={item.id}>
                  <Td>{item.descricao}</Td>
                  <Td>{item.dadosPagamentoAlternativo?.slug || '-'}</Td>
                  <Td>
                    {slug && frontWebUrl ? (
                      <CopyButton
                        size="small"
                        onClick={() => handleCopiarLink(slug)}
                        title="Copiar link de agendamento"
                      >
                        <FileCopyIcon fontSize="small" />
                      </CopyButton>
                    ) : '-'}
                  </Td>
                  <Td>{item.dadosPagamentoAlternativo?.walletId || '-'}</Td>
                  <Td>{item.dadosPagamentoAlternativo?.percentualRepasse ?? '-'}</Td>
                  <Td>{item.ativo ? 'Sim' : 'Não'}</Td>
                  <Td>
                    <IconButton size="small" onClick={() => handleEditar(item)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Td>
                </tr>
              );
            })}
            {convenios.length === 0 && (
              <tr>
                <Td colSpan={7} style={{ textAlign: 'center', color: '#999' }}>
                  Nenhum convênio cadastrado
                </Td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      {modalAberto && (
        <ModalSlider
          isOpen={modalAberto}
          onClose={handleClose}
          title={selecionado ? 'Editar Dados de Pagamento do Convênio' : 'Adicionar Convênio'}
        >
          <ModalConvenioAlternativo
            convenio={selecionado}
            unidade={unidade}
            onClose={handleClose}
            onSuccess={handleSuccess}
            frontWebUrl={frontWebUrl}
          />
        </ModalSlider>
      )}

      <Notification
        close={() => setNotification({ ...notification, isOpen: false })}
        isOpen={notification.isOpen}
        variant={notification.variant}
        message={notification.message}
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.label`
  font-size: 18px;
  font-weight: 700;
`;

const AddButton = styled(Button)`
  background-color: #00B0AE !important;
  color: #fff !important;
  text-transform: none !important;
  font-size: 13px !important;
  padding: 6px 14px !important;
  border-radius: 8px !important;
  box-shadow: 0 2px 6px rgba(0,176,174,0.35) !important;
  &:hover {
    background-color: #009997 !important;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px;
  border-bottom: 2px solid #eee;
  color: #555;
`;

const Td = styled.td`
  padding: 8px;
  border-bottom: 1px solid #f0f0f0;
`;

const CopyButton = styled(IconButton)`
  color: #00B0AE !important;
  &:hover {
    background-color: rgba(0, 176, 174, 0.08) !important;
  }
`;

export default ConveniosAlternativo;
